import {galleryScreenDescription, galleryScreenTitle, isIndexableScreen} from './gallery-indexing';
import {DEFAULT_IMAGE, SITE_URL, resolvePageMetadata, type PageMetadata} from './route-metadata';

// Re-exported so existing importers keep one entry point for page metadata,
// while the route table itself stays DOM-free for the Workers build.
export {
    DEFAULT_DESCRIPTION,
    DEFAULT_IMAGE,
    DEFAULT_TITLE,
    SITE_URL,
    canonicalUrlFor,
    resolvePageMetadata,
    type PageMetadata,
} from './route-metadata';

export interface GalleryScreenMetaSource {
    projectId: number | string;
    screenId: number | string;
    projectTitle: string;
    screenTitle?: string | null;
    platform: string;
    screenWidth: number;
    screenHeight: number;
    previewUrl?: string | null;
}

/**
 * Builds the head data for a gallery detail screen, matching what the
 * Cloudflare function injects on a full page load.
 */
export function galleryScreenMetadata(source: GalleryScreenMetaSource): PageMetadata {
    return {
        title: galleryScreenTitle(source),
        description: galleryScreenDescription(source),
        canonical: `${SITE_URL}/gallery/${source.projectId}/${source.screenId}`,
        indexable: isIndexableScreen(source.projectTitle, source.previewUrl),
    };
}

function upsertMeta(head: HTMLHeadElement, attribute: 'name' | 'property', key: string, content: string) {
    let element = head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
    if (!element) {
        element = head.ownerDocument.createElement('meta');
        element.setAttribute(attribute, key);
        head.append(element);
    }
    element.setAttribute('content', content);
}

export function applyPageMetadata(doc: Document, metadata: PageMetadata) {
    const {head} = doc;
    doc.title = metadata.title;

    upsertMeta(head, 'name', 'description', metadata.description);
    upsertMeta(head, 'property', 'og:type', 'website');
    upsertMeta(head, 'property', 'og:url', metadata.canonical);
    upsertMeta(head, 'property', 'og:title', metadata.title);
    upsertMeta(head, 'property', 'og:description', metadata.description);
    upsertMeta(head, 'property', 'og:image', DEFAULT_IMAGE);
    upsertMeta(head, 'name', 'twitter:card', 'summary_large_image');
    upsertMeta(head, 'name', 'twitter:title', metadata.title);
    upsertMeta(head, 'name', 'twitter:description', metadata.description);
    upsertMeta(head, 'name', 'twitter:image', DEFAULT_IMAGE);

    let canonical = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
        canonical = doc.createElement('link');
        canonical.rel = 'canonical';
        head.append(canonical);
    }
    canonical.setAttribute('href', metadata.canonical);

    const robots = head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (metadata.indexable) {
        robots?.remove();
    } else if (robots) {
        robots.setAttribute('content', 'noindex, follow');
    } else {
        upsertMeta(head, 'name', 'robots', 'noindex, follow');
    }
}

export function installPageMetadata(doc: Document, path: string) {
    const metadata = resolvePageMetadata(path);
    if (metadata) applyPageMetadata(doc, metadata);
}
